import { useTranslation } from 'react-i18next';
import { useLoaderData, useMatches } from 'react-router';
import { WALL_TYPE } from '@cx-sdk/constants';
import { type SubscriptionTerm } from '@cx-sdk/subs';
import { SUBSCRIPTION_TERM } from '@cx-sdk/subs/schema';
import { type RequestContext, type SerializeFrom, type SubsProduct } from '@cx-web/domain-types';
import { useMyOCUser } from '@cx-web/feature-myoc';
import {
  Authentication,
  Dialog as AuthDialog,
  DialogContent,
  DialogTitle,
  InfoLink,
  useAuthModal,
  VIEW_VERIFY_PAYMENT,
} from '@cx-web/feature-myoc-login';
import { STRIPE_PRODUCT_IDS } from '@cx-web/repub-config';
import { getDefaultProduct } from '@cx-web/utils/getDefaultProduct';
import { SubscriptionType, userHasSubscriptionType } from '@cx-web/utils/getUserSubscriptionTypes';
import { Card } from '@fpsm/cxy-components/Card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@fpsm/cxy-components/Tabs';
import clsx from 'clsx';

import { trackLandingPagePlansToggleClick } from '~/components/BettingInsights/tracking';
import FAQ from '~/components/FAQ';
import { navigateToStripeManagement } from '~/features/subscriptions/components/Subscribe/helpers/navigateToStripeManagement';
import SubscriptionCard from '~/features/subscriptions/components/Subscribe/SubscriptionCard';
import { useSubscriptionFlow } from '~/features/subscriptions/hooks/use-subscription-flow.ts';
import { useTrackCTAImpression } from '~/hooks/use-track-cta-impression.ts';
import { createPositiveEvConfig } from '~/pages/config';
import type { loader as rootLoader } from '~/pages/root-loader';

import { useSubscriptionRedirect } from '../shared/hooks/useSubscriptionRedirect';
import { getPostLoginSubscribeProduct } from '../shared/utils/getPostLoginSubscribeProduct';
import landingPageStyles from '../styles/landing-page.module.scss';

import styles from './styles/subscribe.module.css';
import { ESSENTIAL_FEATURES, FAQ_ITEMS, PREMIUM_FEATURES } from './constants';
import { type LoaderDataTwo } from './loader.server';
export { meta } from './meta';

export default function TwoTieredLandingPage() {
  const matches = useMatches();
  const rootMatch = matches.find(match => /oddschecker-plus\/root$/.test(match.id));
  const rootLoaderData = rootMatch?.data as SerializeFrom<typeof rootLoader>;
  const { t } = useTranslation(['label']);
  if (!rootLoaderData) {
    throw new Error('Root loader data is missing');
  }

  const {
    cancelPayment,
    hasMonthlyAndYearlyOptions,
    hasMonthlyOptions,
    isLoggedIn,
    isSubscribed,
    isUpgrade,
    monthlyProducts,
    productPrice,
    redirectPath,
    repub,
    requestContext,
    subscriptionStatus,
    subscriptionTerm,
    subscriptionTypes,
    verifyPayment,
    yearlyProducts,
  } = useLoaderData<LoaderDataTwo>();

  const { repubPath, resourcesUrl, socialLoginIds, url: currentUrl } = rootLoaderData;

  const { myocData } = useMyOCUser();
  const { isOpen, toggle, view } = useAuthModal();

  const defaultMonthlyProduct = getDefaultProduct(monthlyProducts);
  const availableProducts = [...monthlyProducts, ...yearlyProducts];

  const { searchParams, shouldRedirectToPaymentPortal } = useSubscriptionRedirect(isSubscribed, cancelPayment);

  const config = createPositiveEvConfig(requestContext as RequestContext);
  const isPaywalled = config.isWalled && config.wallType === WALL_TYPE.PAYWALL;
  const isVerifyingPayment = view === VIEW_VERIFY_PAYMENT;

  const hasMonthlyEssential = userHasSubscriptionType(subscriptionTypes, SubscriptionType.MONTHLY_ESSENTIAL);
  const hasYearlyEssential = userHasSubscriptionType(subscriptionTypes, SubscriptionType.YEARLY_ESSENTIAL);

  const canUpgrade = isSubscribed && (hasMonthlyEssential || hasYearlyEssential);

  // Use shared subscription flow hook with upgrade customization
  const { handleProductClick, userHasCancelledSubscription } = useSubscriptionFlow(
    {
      canUpgrade,
      cancelPayment,
      isLoggedIn,
      isSubscribed,
      productPrice,
      redirectPath,
      repub,
      repubPath,
      requestContext,
      subscriptionTerm: (subscriptionTerm ?? undefined) as SubscriptionTerm | undefined,
      userId: myocData?.myOCUser?.userID,
      verifyPayment,
    },
    {
      availableProducts,
      getSelectionLabel: product => product.name as 'Essential' | 'Premium',
      onBeforeSubscribe: product => {
        // Handle upgrade flow for essential subscribers
        if (subscriptionStatus.hasEssentialSubscription) {
          navigateToStripeManagement({
            isUpgrade: hasMonthlyEssential || hasYearlyEssential,
            repub,
            upgradeProduct: product,
          });
          return false; // Stop normal flow
        }
        return true; // Continue normal flow
      },
      onPostLoginSubscribe: () =>
        getPostLoginSubscribeProduct(
          shouldRedirectToPaymentPortal,
          searchParams,
          availableProducts,
          defaultMonthlyProduct,
        ),
    },
  );

  const ref = useTrackCTAImpression({
    wallType: isUpgrade ? 'plan_upgrade_cta' : 'landing_page_cta',
    title: isUpgrade ? 'upgrade_component' : 'paywall_component',
  });

  const renderSubscriptionCards = (products: SubsProduct[], hasEssential: boolean) => {
    const premiumProduct = products.find(
      p => p.productId === STRIPE_PRODUCT_IDS.PREMIUM_MONTHLY || p.productId === STRIPE_PRODUCT_IDS.PREMIUM_ANNUALLY,
    );
    const essentialProduct = products.find(
      p =>
        p.productId === STRIPE_PRODUCT_IDS.ESSENTIAL_MONTHLY || p.productId === STRIPE_PRODUCT_IDS.ESSENTIAL_ANNUALLY,
    );

    return (
      <SubscriptionCard.Container>
        {premiumProduct && (
          <SubscriptionCard
            featured
            features={PREMIUM_FEATURES}
            handleSubscribe={args => {
              handleProductClick(args);
            }}
            isEssentialSubscriber={hasMonthlyEssential || hasYearlyEssential}
            isLoggedIn={isLoggedIn}
            pricingLabel="Less than £1 per day"
            product={premiumProduct}
            title="PREMIUM"
            userHasCancelledSubscription={userHasCancelledSubscription}
            variant="premium"
          />
        )}
        {!hasEssential && essentialProduct && (
          <SubscriptionCard
            features={ESSENTIAL_FEATURES}
            handleSubscribe={args => {
              handleProductClick(args);
            }}
            isLoggedIn={isLoggedIn}
            pricingLabel="Less than 35p per day"
            product={essentialProduct}
            title="ESSENTIAL"
            userHasCancelledSubscription={userHasCancelledSubscription}
            variant="essential"
          />
        )}
      </SubscriptionCard.Container>
    );
  };

  let SubscriptionCards = hasMonthlyAndYearlyOptions ? (
    <Tabs defaultValue="monthly">
      <TabsList className={styles.tabsList} variant="block">
        <TabsTrigger
          onClick={() => {
            trackLandingPagePlansToggleClick({
              selection: SUBSCRIPTION_TERM.MONTHLY,
            });
          }}
          value="monthly"
          variant="block"
        >
          Monthly
        </TabsTrigger>
        <TabsTrigger
          onClick={() => {
            trackLandingPagePlansToggleClick({
              selection: 'annual',
            });
          }}
          value="annual"
          variant="block"
        >
          Annual
        </TabsTrigger>
      </TabsList>
      <TabsContent value="monthly">{renderSubscriptionCards(monthlyProducts, hasMonthlyEssential)}</TabsContent>
      <TabsContent value="annual">{renderSubscriptionCards(yearlyProducts, hasYearlyEssential)}</TabsContent>
    </Tabs>
  ) : hasMonthlyOptions ? (
    renderSubscriptionCards(monthlyProducts, hasMonthlyEssential)
  ) : (
    renderSubscriptionCards(yearlyProducts, hasYearlyEssential)
  );

  return (
    <>
      <div className={landingPageStyles.gradient} />

      <main className={clsx(landingPageStyles.home, landingPageStyles['home--narrow'], landingPageStyles['home--uk'])}>
        <div className={landingPageStyles.hero} data-testid="hero-content" ref={ref}>
          <div className={landingPageStyles.heroContent}>
            <div
              className={clsx(
                landingPageStyles.textContainer,
                landingPageStyles['textContainer--narrow'],
                styles.textContainer,
              )}
            >
              <h1 className={landingPageStyles.title}>
                {hasMonthlyEssential ? 'Upgrade to Oddschecker+ for under £1 per day' : 'GET OC+ FREE FOR 7 DAYS'}
              </h1>
              <h2 className={landingPageStyles.subtitle}>{t('CX_PLUS_LANDING_TWO_TIER_SUBTITLE')}</h2>
            </div>
          </div>
          <picture className={landingPageStyles.heroImage}>
            <img alt="" data-testid="hero-image" src={`${resourcesUrl}/assets/img/subscribe/hero-v2.png`} />
          </picture>
        </div>

        <Card className={styles.subscriptionCardContainer} variant="secondary">
          {SubscriptionCards}
        </Card>

        <FAQ>
          <FAQ.Title>FAQs</FAQ.Title>
          <FAQ.Subtitle>Discover answers to frequently asked questions about oddschecker+</FAQ.Subtitle>
          <FAQ.List items={FAQ_ITEMS} />
        </FAQ>

        <AuthDialog onOpenChange={toggle} open={isOpen}>
          <DialogContent aria-describedby={undefined}>
            <DialogTitle style={{ display: 'none' }}>Log in or Sign up</DialogTitle>
            <Authentication
              currentUrl={currentUrl}
              googleID={socialLoginIds?.googleID ?? ''}
              infoLink={isPaywalled && !isVerifyingPayment ? <InfoLink /> : undefined}
              resourcesUrl={resourcesUrl}
              view={view}
            />
          </DialogContent>
        </AuthDialog>
      </main>
    </>
  );
}
